"use client";

import { useEffect, useState, useCallback } from "react";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useMutationWithAuthRedirect } from "@/lib/convexAuth";
import type { Id } from "../../../../../convex/_generated/dataModel";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { VisibilityOption } from "@/components/organizations/VisibilityOption";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { SettingsIcon, GlobeIcon, LockIcon, ArrowRightIcon, TrashIcon, CrownIcon } from "@/components/icons";

export default function OrganizationSettingsPage() {
  const params = useParams();
  const router = useRouter();
  const slug = params.slug as string;

  const org = useQuery(api.organizations.getBySlug, { slug });
  const membership = useQuery(
    api.members.getCurrentMembership,
    org ? { organizationId: org._id } : "skip"
  );
  const members = useQuery(
    api.members.getByOrganization,
    org ? { organizationId: org._id } : "skip"
  );
  const updateOrg = useMutationWithAuthRedirect(api.organizations.update);
  const linkServer = useMutationWithAuthRedirect(api.gameServers.linkFromCode);
  const removeOrg = useMutationWithAuthRedirect(api.organizations.remove);
  const transferOwnership = useMutationWithAuthRedirect(api.organizations.transferOwnership);

  const [initialized, setInitialized] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [serverIp, setServerIp] = useState("");
  const [serverPort, setServerPort] = useState("");
  const [visibility, setVisibility] = useState<"public" | "page_only" | "private">("public");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [serverLinkCode, setServerLinkCode] = useState("");
  const [serverLinking, setServerLinking] = useState(false);
  const [serverLinkError, setServerLinkError] = useState<string | null>(null);
  const [serverLinkSuccess, setServerLinkSuccess] = useState<string | null>(null);

  // Delete org state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Transfer ownership state
  const [transferTargetId, setTransferTargetId] = useState<Id<"users"> | "">("");
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  useEffect(() => {
    if (!org || initialized) return;
    setName(org.name ?? "");
    setDescription(org.description ?? "");
    setServerIp(org.serverIp ?? "");
    setServerPort(org.serverPort ? String(org.serverPort) : "");
    setVisibility(org.visibility);
    setInitialized(true);
  }, [org, initialized]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!org) return;

    setError("");
    setSaved(false);

    if (!name.trim()) {
      setError("Organization name is required");
      return;
    }

    let parsedPort: number | undefined;
    if (serverPort.trim()) {
      parsedPort = Number(serverPort);
      if (Number.isNaN(parsedPort)) {
        setError("Server port must be a number");
        return;
      }
    }

    setSaving(true);
    try {
      await updateOrg({
        id: org._id,
        name: name.trim(),
        description: description.trim() || undefined,
        serverIp: serverIp.trim() || undefined,
        serverPort: parsedPort,
        visibility,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update organization");
    } finally {
      setSaving(false);
    }
  };

  const handleLinkServer = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!org) return;

    setServerLinkError(null);
    setServerLinkSuccess(null);

    const code = serverLinkCode.trim();
    if (!code) {
      setServerLinkError("Code is required");
      return;
    }

    setServerLinking(true);
    try {
      await linkServer({ organizationId: org._id, code });
      setServerLinkCode("");
      setServerLinkSuccess("Server linked successfully");
    } catch (err) {
      setServerLinkError(err instanceof Error ? err.message : "Failed to link server");
    } finally {
      setServerLinking(false);
    }
  };

  const isOwner = membership?.role === "owner";

  const handleDeleteOrg = useCallback(async () => {
    if (!org) return;
    setDeleting(true);
    try {
      await removeOrg({ id: org._id });
      router.push("/servers");
    } catch (err) {
      console.error("Failed to delete organization:", err);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [org, removeOrg, router]);

  const handleTransferOwnership = useCallback(async () => {
    if (!org || !transferTargetId) return;
    setTransferring(true);
    setTransferError(null);
    try {
      await transferOwnership({
        organizationId: org._id,
        newOwnerId: transferTargetId as Id<"users">,
      });
      setShowTransferConfirm(false);
      setTransferTargetId("");
    } catch (err) {
      setTransferError(
        err instanceof Error ? err.message : "Failed to transfer ownership"
      );
    } finally {
      setTransferring(false);
    }
  }, [org, transferTargetId, transferOwnership]);

  // Members eligible for ownership transfer (non-owner active members)
  const transferCandidates = members?.filter(
    (m) => m.role !== "owner" && m.user
  );

  if (org === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 px-4 py-10">
          <div className="mx-auto max-w-3xl">
            <Skeleton className="h-72" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (org === null) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardContent className="py-10 text-center">
              <h2 className="text-xl font-semibold">Organization Not Found</h2>
              <p className="mt-2 text-muted">
                This organization doesn&apos;t exist or has been deleted.
              </p>
              <Link href="/servers" className="mt-6 inline-block">
                <Button>Browse Servers</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (membership === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 px-4 py-10">
          <div className="mx-auto max-w-3xl">
            <Skeleton className="h-72" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isAdmin =
    membership &&
    membership.status === "active" &&
    ["owner", "admin"].includes(membership.role);

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardContent className="py-10 text-center">
              <h2 className="text-xl font-semibold">Access Restricted</h2>
              <p className="mt-2 text-muted">
                Only organization owners and admins can access settings.
              </p>
              <Link href={`/org/${org.slug}`} className="mt-6 inline-block">
                <Button>Back to Organization</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1 px-4 py-10">
        <div className="mx-auto max-w-3xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
                <SettingsIcon className="h-6 w-6" />
                Organization Settings
              </h1>
              <p className="mt-1 text-muted">
                Update your organization profile and visibility.
              </p>
            </div>
            <Link href={`/org/${org.slug}`}>
              <Button variant="outline">
                View Organization
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Keep your details current for members.</CardDescription>
            </CardHeader>
            <form onSubmit={handleSave}>
              <CardContent className="space-y-6">
                {error && (
                  <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    Organization Name *
                  </label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={saving}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="slug" className="text-sm font-medium">
                    URL Slug
                  </label>
                  <Input id="slug" value={org.slug} disabled />
                </div>

                <div className="space-y-2">
                  <label htmlFor="description" className="text-sm font-medium">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="flex min-h-[90px] w-full rounded-lg border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={saving}
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium">Server Connection</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label htmlFor="serverIp" className="text-sm font-medium">
                        Server IP
                      </label>
                      <Input
                        id="serverIp"
                        value={serverIp}
                        onChange={(e) => setServerIp(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="serverPort" className="text-sm font-medium">
                        Port
                      </label>
                      <Input
                        id="serverPort"
                        type="number"
                        value={serverPort}
                        onChange={(e) => setServerPort(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium">Link Game Server</h3>
                  <p className="text-sm text-muted">
                    In-game (as OP), run <code>/link server</code> to get a 6-digit code,
                    then enter it here to link the server to this organization.
                  </p>

                  {org.serverId ? (
                    <div className="rounded-lg border border-border bg-foreground/5 p-3 text-sm">
                      <p className="font-medium text-foreground">Linked</p>
                      <p className="text-muted">
                        Server ID: <code>{org.serverId}</code>
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-border bg-foreground/5 p-3 text-sm text-muted">
                      No server linked yet.
                    </div>
                  )}

                  <form onSubmit={handleLinkServer} className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={serverLinkCode}
                        onChange={(e) => setServerLinkCode(e.target.value)}
                        placeholder="123456"
                        disabled={serverLinking}
                        className="flex-1"
                      />
                      <Button
                        type="submit"
                        disabled={serverLinking || !serverLinkCode.trim()}
                      >
                        {serverLinking ? "Linking..." : "Link Server"}
                      </Button>
                    </div>
                    {serverLinkError && (
                      <p className="text-sm text-destructive">{serverLinkError}</p>
                    )}
                    {serverLinkSuccess && (
                      <p className="text-sm text-primary">{serverLinkSuccess}</p>
                    )}
                  </form>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium">Visibility</h3>
                  <div className="grid gap-3">
                    <VisibilityOption
                      value="public"
                      selected={visibility}
                      onSelect={setVisibility}
                      icon={<GlobeIcon className="h-5 w-5" />}
                      title="Public"
                      description="Anyone can discover your organization."
                    />
                    <VisibilityOption
                      value="page_only"
                      selected={visibility}
                      onSelect={setVisibility}
                      icon={<GlobeIcon className="h-5 w-5 opacity-50" />}
                      title="Unlisted"
                      description="Only accessible with a direct link."
                    />
                    <VisibilityOption
                      value="private"
                      selected={visibility}
                      onSelect={setVisibility}
                      icon={<LockIcon className="h-5 w-5" />}
                      title="Private"
                      description="Invite only. Hidden from Server Finder."
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button type="submit" disabled={saving}>
                    {saving ? "Saving..." : saved ? "Saved" : "Save Changes"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => router.back()}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>

          {/* Danger Zone - Owner Only */}
          {isOwner && (
            <Card className="border-destructive/30">
              <CardHeader>
                <CardTitle className="text-destructive">Danger Zone</CardTitle>
                <CardDescription>
                  Irreversible actions. Proceed with caution.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Transfer Ownership */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CrownIcon className="h-5 w-5 text-yellow-500" />
                    <h3 className="font-medium">Transfer Ownership</h3>
                  </div>
                  <p className="text-sm text-muted">
                    Transfer ownership to another member. You will be demoted to
                    admin.
                  </p>
                  {transferCandidates && transferCandidates.length > 0 ? (
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <select
                        className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        value={transferTargetId}
                        onChange={(e) =>
                          setTransferTargetId(e.target.value as Id<"users"> | "")
                        }
                      >
                        <option value="">Select a member...</option>
                        {transferCandidates.map((m) => (
                          <option key={m.user!.id} value={m.user!.id}>
                            {m.user!.username || "anonymous"} ({m.role})
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!transferTargetId || transferring}
                        onClick={() => setShowTransferConfirm(true)}
                      >
                        {transferring ? "Transferring..." : "Transfer"}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted">
                      No other members to transfer ownership to.
                    </p>
                  )}
                  {transferError && (
                    <p className="text-sm text-destructive">{transferError}</p>
                  )}
                </div>

                <hr className="border-border" />

                {/* Delete Organization */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <TrashIcon className="h-5 w-5 text-destructive" />
                    <h3 className="font-medium">Delete Organization</h3>
                  </div>
                  <p className="text-sm text-muted">
                    Permanently delete this organization and all its data. This
                    action cannot be undone.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete Organization
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Transfer Ownership Confirmation */}
      <ConfirmDialog
        open={showTransferConfirm}
        onConfirm={handleTransferOwnership}
        onCancel={() => setShowTransferConfirm(false)}
        title="Transfer ownership"
        description={`Are you sure you want to transfer ownership of "${org.name}" to ${transferCandidates?.find((m) => m.user?.id === transferTargetId)?.user?.username ?? "this member"}? You will be demoted to admin.`}
        confirmText={transferring ? "Transferring..." : "Transfer Ownership"}
        cancelText="Cancel"
        variant="destructive"
      />

      {/* Delete Organization Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-destructive">
                Delete Organization
              </CardTitle>
              <CardDescription>
                This will permanently delete <strong>{org.name}</strong> and all
                its data, including messages, members, and invites.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Type <strong>{org.name}</strong> to confirm:
                </label>
                <Input
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder={org.name}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={deleteConfirmName !== org.name || deleting}
                  onClick={handleDeleteOrg}
                >
                  {deleting ? "Deleting..." : "Delete Forever"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmName("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
}
